/**
 * The pure model of the tenant custom domains: the Custom domains card on the tenant Overview tab
 * and the host of the title-row link (plan 2026-10-12-tenant-custom-domain-read, WS-4).
 *
 * The data comes from E22 (`getTenantRootShellHosts` in `tenants.api.ts`), one row per data plane
 * placement. Each host string is data plane data. The card renders it as text only, and a host
 * reaches an `href` only when {@link isHostName} accepts it. Every function here is pure: no
 * request, no clock read and no storage. Every sentence here is a UI string, so none holds an em
 * dash. Console copy says "Custom domains"; code says `root-shell-hosts` (decision D9).
 */
import type { RootShellHost, RootShellHostsByPlacement, RootShellHostsPlacement } from '../api/tenants.api';
import type { AsyncState } from './async-state';
import type { BadgeTone } from './tenants-model';

/** One DNS label: lowercase letters, digits and inner hyphens, 63 characters at most. */
const DNS_LABEL = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

/** The longest host name DNS carries, in characters. */
const HOST_NAME_MAX_LENGTH = 253;

/**
 * `true` when `host` is a host name the console may put in an `href`: 253 characters at most, two
 * labels or more, each label a lowercase DNS label, and a last label that is not all digits. So
 * an IP address, a port, a path, an uppercase letter and a single label all give `false`. This is
 * the grammar of the served-hosts document (`SERVED_HOST_PATTERN`), applied again in the browser,
 * because the console does not parse the E22 body. No side effect.
 */
export function isHostName(host: string): boolean {
  if (host.length > HOST_NAME_MAX_LENGTH) return false;
  const labels = host.split('.');
  if (labels.length < 2 || !labels.every((label) => DNS_LABEL.test(label))) return false;
  return !/^[0-9]+$/.test(labels[labels.length - 1] as string);
}

/**
 * The state of one custom domain, from the two facts the data plane reports (decision D10):
 *
 * - `waiting`: `verifiedAt` is `null`. The TXT proof did not match yet. This wins over `bound`,
 *   because a host with no proof is not the org's host.
 * - `verified`: `verifiedAt` is set and `bound` is `true`. The data plane lists the host for the
 *   org: it serves it, or its certificate is pending. The console cannot tell which.
 * - `verified-unbound`: `verifiedAt` is set and `bound` is `false`.
 */
export type RootShellHostState = 'waiting' | 'verified' | 'verified-unbound';

/** The state of `host`. See {@link RootShellHostState}. Pure. */
export function rootShellHostState(host: Pick<RootShellHost, 'verifiedAt' | 'bound'>): RootShellHostState {
  if (host.verifiedAt === null) return 'waiting';
  return host.bound ? 'verified' : 'verified-unbound';
}

/** The badge word of each state, as the console prints it. */
export const ROOT_SHELL_HOST_STATE_LABEL: Readonly<Record<RootShellHostState, string>> = {
  waiting: 'Waiting for DNS proof',
  verified: 'Verified',
  'verified-unbound': 'Verified, not served yet',
};

/**
 * The badge tone of each state. `waiting` is `neutral`: an org admin started a claim, and that is
 * no fault. `verified-unbound` is `warning`: the proof matched and the data plane does not list
 * the host yet.
 */
export const ROOT_SHELL_HOST_STATE_TONE: Readonly<Record<RootShellHostState, BadgeTone>> = {
  waiting: 'neutral',
  verified: 'positive',
  'verified-unbound': 'warning',
};

// ── The sentences of the card. Each one is a UI string, so none holds an em dash. ──

/** The hint under "No data-plane placement": the tenant has no placement to ask. */
export const ROOT_SHELL_UNPLACED_HINT =
  'This tenant is not placed on a data plane, so no data plane holds a custom domain for it.';

/** The reason under "Custom domains not collected", for a placement with the status `unsupported`. */
export const ROOT_SHELL_UNSUPPORTED_REASON =
  'This data plane runs a release from before the custom domain read, so it cannot report them. An upgrade of the data plane adds the read.';

/** The message of the error state, for a placement with the status `unreachable`. */
export const ROOT_SHELL_UNREACHABLE_MESSAGE =
  'The control plane has no usable answer from this data plane. The data plane is out of service, did not answer in time, or sent an answer that failed the contract check.';

/** The hint for a data plane that answered and holds no org with the tenant's slug. */
export const ROOT_SHELL_ORG_MISSING_HINT =
  'This data plane answered, and it holds no organization with the slug of this tenant. So it serves no custom domain for it.';

/** The hint under "No custom domains": the org exists on the data plane and has no host row. */
export const ROOT_SHELL_NONE_HINT =
  'No organization admin has added a custom domain on this data plane. An admin adds one in the root shell settings.';

/** The note under a list of hosts: what "Verified" can and cannot prove (limit 8.2 of the plan). */
export const ROOT_SHELL_BOUND_NOTE =
  'Verified means the TXT proof matched and the data plane lists the host for this organization. The control plane cannot tell whether its certificate is live yet.';

/** One row of the card: the host as the data plane sent it, its state and its two times. */
export interface RootShellHostRow {
  readonly host: string;
  readonly state: RootShellHostState;
  /** ISO time of the TXT match, or `null` while the host waits for its proof. */
  readonly verifiedAt: string | null;
  /** ISO time the org admin added the host. */
  readonly assignedAt: string;
}

/* The sort key of an ISO time: its epoch milliseconds, or the end of time when it does not parse. */
function timeKey(iso: string): number {
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? Number.POSITIVE_INFINITY : ms;
}

/**
 * The rows of one placement, the oldest `assignedAt` first. Two hosts with the same time keep the
 * order of the answer, and a time that does not parse sorts last. Pure.
 */
export function rootShellHostRows(hosts: readonly RootShellHost[]): readonly RootShellHostRow[] {
  return hosts
    .map((host, index) => ({ host, index }))
    .sort((a, b) => timeKey(a.host.assignedAt) - timeKey(b.host.assignedAt) || a.index - b.index)
    .map(({ host }) => ({
      host: host.host,
      state: rootShellHostState(host),
      verifiedAt: host.verifiedAt,
      assignedAt: host.assignedAt,
    }));
}

/**
 * What the card shows for one placement. Only `status: 'ok'` shows hosts:
 *
 * - `unsupported`: the data plane answered 404. It runs a release from before this read.
 * - `unreachable`: the data plane gave no answer inside the deadline, or an answer the contract
 *   refused. A status string the console does not know takes this arm too, so it shows no host.
 * - `org-missing`: the data plane answered and holds no org with the tenant's slug.
 * - `none`: the data plane holds the org and the org has no custom domain.
 * - `hosts`: the org has one custom domain or more, in {@link rootShellHostRows} order.
 *
 * `canonicalHost` is the host of the org's token issuer on that data plane, as sent, or `null`.
 */
export type RootShellPlacementView =
  | { readonly kind: 'unsupported' }
  | { readonly kind: 'unreachable' }
  | { readonly kind: 'org-missing' }
  | { readonly kind: 'none'; readonly canonicalHost: string | null }
  | { readonly kind: 'hosts'; readonly canonicalHost: string | null; readonly rows: readonly RootShellHostRow[] };

/** The view of one placement row. See {@link RootShellPlacementView}. Pure. */
export function rootShellPlacementView(placement: RootShellHostsPlacement): RootShellPlacementView {
  if (placement.status === 'ok') {
    if (!placement.orgFound) return { kind: 'org-missing' };
    if (placement.hosts.length === 0) return { kind: 'none', canonicalHost: placement.canonicalHost };
    return { kind: 'hosts', canonicalHost: placement.canonicalHost, rows: rootShellHostRows(placement.hosts) };
  }
  if (placement.status === 'unsupported') return { kind: 'unsupported' };
  return { kind: 'unreachable' };
}

/**
 * The host the title-row link prefers before the data plane host, or `null`:
 *
 * 1. The first `verified` custom domain, over the `ok` placements in placement order and the rows
 *    of each in {@link rootShellHostRows} order.
 * 2. Else the first `canonicalHost` of an `ok` placement that holds the org.
 *
 * A candidate that {@link isHostName} refuses is skipped, and the next one is tried. A placement
 * with any other status gives no candidate. Pure.
 */
export function preferredRootShellHost(answer: RootShellHostsByPlacement): string | null {
  const views = answer.placements.map(rootShellPlacementView);
  for (const view of views) {
    if (view.kind !== 'hosts') continue;
    const found = view.rows.find((row) => row.state === 'verified' && isHostName(row.host));
    if (found !== undefined) return found.host;
  }
  for (const view of views) {
    if ((view.kind === 'hosts' || view.kind === 'none') && view.canonicalHost !== null && isHostName(view.canonicalHost)) {
      return view.canonicalHost;
    }
  }
  return null;
}

/**
 * An E22 answer with the org id it was read for. The tenant drill-in stores this pair, because
 * the body carries no org id and a read for the previous tenant can still be on screen for one
 * render after the route changes.
 */
export interface TenantRootShellHostsRead {
  readonly orgId: string;
  readonly answer: RootShellHostsByPlacement;
}

/**
 * The E22 state of the tenant `orgId`. A ready read for another org reads as `loading`, so no
 * card and no link shows another tenant's hosts. The id comparison ignores case, as the pane's
 * comparison of the route id and the stored id does. `loading` and `error` pass through. Pure.
 */
export function rootShellHostsForTenant(
  state: AsyncState<TenantRootShellHostsRead>,
  orgId: string,
): AsyncState<RootShellHostsByPlacement> {
  if (state.kind !== 'ready') return state;
  if (state.data.orgId.toLowerCase() !== orgId.toLowerCase()) return { kind: 'loading' };
  return { kind: 'ready', data: state.data.answer };
}
